import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { I18nModule, I18nService } from '@recruitee/i18n';
import { IntegrationKinds } from '@recruitee/integrations/types';
import {
  AddNewButtonComponent,
  DialogService,
  DialogWidth,
  DividerComponent,
  HeadingComponent,
  SpinnerComponent,
} from '@recruitee/user-interface';

import { IntegrationListItemConfig } from '../../misc/types';
import { FormConfigStoreService } from '../data/src/form-config/form-config.service';
import { RemoteTemplateStoreService } from '../data/src/remote-template/remote-template.service';
import { SourceStoreService } from '../data/src/source/source.service';
import { FieldMappingFormComponent } from '../field-mapping-form/field-mapping-form.component';
import { FormConfig, FormConfigMode } from '../types';

@Component({
  selector: 'rti-field-mapping-settings',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './field-mapping-settings.component.html',
  styleUrls: ['./field-mapping-settings.component.less'],
  standalone: true,
  imports: [
    AddNewButtonComponent,
    CommonModule,
    DividerComponent,
    FieldMappingFormComponent,
    HeadingComponent,
    I18nModule,
    SpinnerComponent,
  ],
})
export class FieldMappingSettingsComponent {
  @Input() public config: IntegrationListItemConfig;
  @Input() public credentialize: () => void;

  public formConfigs: FormConfig[] = [];

  public formsLoading: boolean;

  public dynamicMode: boolean = false;

  public expandedFormConfigId: number | null = null;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private formConfigStore: FormConfigStoreService,
    private sourceStore: SourceStoreService,
    private remoteTemplateStoreService: RemoteTemplateStoreService,
  ) {}

  public get integrationName(): string {
    if ('name' in this.config) {
      return this.config.name!;
    } else {
      return this.i18n.translate(`rti.${this.kind}.name`);
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    this.dynamicMode = this.config.fieldMapping?.mode === FormConfigMode.DYNAMIC;

    this.formConfigStore
      .getFormConfigsLoading(this.config.kind)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(isLoading => {
        this.formsLoading = isLoading;

        this.cdRef.detectChanges();
      });

    this.formConfigStore
      .getFormConfigs(this.config.kind)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(formConfigs => {
        this.formConfigs = formConfigs;

        this.cdRef.detectChanges();
      });

    this.formConfigStore.fetchFormConfigs(this.config.kind);
    this.sourceStore.fetchSources();

    if (this.dynamicMode) {
      this.remoteTemplateStoreService.fetchRemoteTemplates(this.config.kind);
    }
  }

  public async openDialog(): Promise<void> {
    if (!this.config?.credentialized) {
      this.credentialize();

      return;
    }

    const { FieldMappingAddTemplateDialogComponent } = await import(
      '../field-mapping-add-template-dialog/field-mapping-add-template-dialog.component'
    );
    this.dialogService
      .openCustom(
        FieldMappingAddTemplateDialogComponent,
        { width: DialogWidth.SmallX },
        {
          formConfigs: this.formConfigs,
          integrationKind: this.kind,
          integrationName: this.integrationName,
        },
      )
      .afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(formConfig => {
        if (formConfig) {
          this.formConfigs.push(formConfig);

          this.expandedFormConfigId = formConfig.id;

          this.cdRef.detectChanges();
        }
      });
  }

  public handleFormConfigDeletion(id: number): void {
    this.formConfigs = this.formConfigs.filter(fc => fc.id !== id);

    this.cdRef.detectChanges();
  }

  public handleFormConfigExpanded(id: number): void {
    this.expandedFormConfigId = id;

    this.cdRef.detectChanges();
  }

  public handleFormConfigClosed(): void {
    this.expandedFormConfigId = null;

    this.cdRef.detectChanges();
  }

  public isFormConfigExpanded(formConfig: FormConfig): boolean {
    return formConfig.id === this.expandedFormConfigId;
  }

  public get kind(): IntegrationKinds {
    return this.config.kind;
  }

  public trackByIndex(index: number): number {
    return index;
  }
}
